"use client";

import { useEffect, useLayoutEffect, useRef } from "react";

import { cn } from "@/lib/utils";
import type { RealityModelPhase } from "@/lib/reality-model/status";

import styles from "./reality-model-mark.module.css";
import { REALITY_MODEL_OUTLINES } from "./reality-model-outlines";

/**
 * The Reality Model's signature mark: the Scirix R built from columns of dots
 * that grow from the smallest on the left into the R's stem. The dots are its
 * sign of life, one motion per phase:
 *
 * - Not built yet: a grey outline of the R; the dots are rings breathing slowly.
 * - Starting: columns light up one by one toward the R.
 * - Updating: a fast wave from the small dots into the R. At six rows, finished
 *   steps fill their row and later rows stay faint; four-row artwork maps each
 *   step to the nearest row.
 * - Up to date: all dots breathe together every 6.5 s; one settle pulse runs
 *   through them when an update finishes.
 * - Update failed: red; the dots get knocked out of their grid, hold, and are
 *   pulled back, every 3.2 s.
 * - Update stopped: grey; the dots jolt back from the R and shake out, every 2.8 s.
 *
 * Four hand-drawn artworks (16, 32, 48 and large) are picked by rendered size.
 * Every mark on the page runs off one shared animation frame loop and writes
 * its dots straight to the DOM, so animating never re-renders React. With
 * reduced motion every phase is drawn still.
 *
 * Geometry, timing and motion are ported from the "Reality Mark Motion" study
 * (claude.ai/artifact/CKaBreVN3o85ok8YHbDnif).
 */

type Point = readonly [number, number];
interface Cell {
  col: number;
  row: number;
  cx: number;
  cy: number;
  r?: number;
  pts?: readonly Point[];
  /** The stem dots are part of the R's letterform and never move. */
  stem?: boolean;
}
type ArtworkKey = "s16" | "s32" | "s48" | "large";
interface Artwork {
  key: ArtworkKey;
  vb: readonly [number, number];
  ncol: number;
  rows: number;
  body: string;
  cells: readonly Cell[];
}

function hexPts(x0: number, x1: number, ys0: number, ys1: number, t0: number, t1: number): Point[] {
  const c = (x0 + x1) / 2;
  return [[x0, ys0], [x0, ys1], [c, t1], [x1, ys1], [x1, ys0], [c, t0]];
}
function octPts(x: number, y: number, w: number, h: number, c: number): Point[] {
  return [
    [x + c, y], [x + w - c, y], [x + w, y + c], [x + w, y + h - c],
    [x + w - c, y + h], [x + c, y + h], [x, y + h - c], [x, y + c],
  ];
}
const range = (n: number) => Array.from({ length: n }, (_, i) => i);

const ARTWORK: Record<ArtworkKey, Artwork> = {
  s16: {
    key: "s16",
    vb: [16, 16],
    ncol: 3,
    rows: 4,
    body: "M11,10H12.5c1.1,0,2,0,2.6-.6.6-.5.9-1.3.9-2.4v-3.5c0-1.4-.3-2.3-1.1-2.8-.8-.5-1.8-.7-3.1-.7h-2.8l-2,1v2l2,1-2,1v2l2,1-2,1v2l2,1-2,1v2l2,1h7v-1L11,10Z M12,3h1v4h-1Z",
    cells: range(4).flatMap((r) => {
      const y = r * 4;
      return [
        { col: 0, row: r, cx: 1, cy: y + 2, pts: hexPts(0, 2, y + 1.5, y + 2.5, y + 1, y + 3) },
        { col: 1, row: r, cx: 4.5, cy: y + 2, pts: hexPts(3, 6, y + 1.2, y + 2.8, y + 0.5, y + 3.5) },
      ];
    }),
  },
  s32: {
    key: "s32",
    vb: [32, 32],
    ncol: 3,
    rows: 4,
    body: "M18,32V0H26A6,6 0 0 1 32,6V15A5,5 0 0 1 27,20H22L32,30V32Z M24,6h2v8h-2Z",
    cells: range(4).flatMap((r) => {
      const y = r * 8;
      return [
        { col: 0, row: r, cx: 2, cy: y + 4, pts: octPts(0, y + 2, 4, 4, 1) },
        { col: 1, row: r, cx: 9, cy: y + 4, pts: octPts(6, y + 1, 6, 6, 2) },
        { col: 2, row: r, cx: 18, cy: y + 4, pts: octPts(14, y, 8, 8, 2), stem: true },
      ];
    }),
  },
  s48: {
    key: "s48",
    vb: [48, 48],
    ncol: 4,
    rows: 6,
    body: "M23,48V0H32C35.9,0 39,0.7 41.3,2 43.7,3.3 45,6.1 45,10.4V21C45,24.3 44.1,26.7 42.3,28.3 40.5,29.9 38,30.8 34.5,31H31L48,48Z M33,9A2,2 0 0 1 35,11V19A2,2 0 0 1 31,19V11A2,2 0 0 1 33,9Z",
    cells: range(6).flatMap((r) => {
      const y = 4 + r * 8;
      return [
        { col: 0, row: r, cx: 1.5, cy: y, r: 1.5 },
        { col: 1, row: r, cx: 8, cy: y, r: 2 },
        { col: 2, row: r, cx: 15, cy: y, r: 3 },
        { col: 3, row: r, cx: 23, cy: y, r: 4, stem: true },
      ];
    }),
  },
  large: {
    key: "large",
    vb: [51.56, 50.4],
    ncol: 4,
    rows: 6,
    body: "M24.34,50.4V0h9.11c4.08,0,7.37.7,9.86,2.11,2.5,1.4,3.74,4.34,3.74,8.8v10.91c0,3.43-.93,5.97-2.79,7.61-1.86,1.64-4.57,2.59-8.12,2.83h-1.62l17.03,18.14Z M35.23,9.85a2.11,2.11 0 0 1 2.11,2.11v8.2a2.11,2.11 0 0 1 -4.22,0v-8.2a2.11,2.11 0 0 1 2.11,-2.11Z",
    cells: range(6).flatMap((r) => {
      const y = 4.2 + r * 8.4;
      return [
        { col: 0, row: r, cx: 1.73, cy: y, r: 1.73 },
        { col: 1, row: r, cx: 8.38, cy: y, r: 2.45 },
        { col: 2, row: r, cx: 16.04, cy: y, r: 3.46 },
        { col: 3, row: r, cx: 24.34, cy: y, r: 4.2, stem: true },
      ];
    }),
  },
};

function artworkFor(size: number): Artwork {
  if (size < 24) return ARTWORK.s16;
  if (size < 40) return ARTWORK.s32;
  if (size < 60) return ARTWORK.s48;
  return ARTWORK.large;
}

const TONE: Record<RealityModelPhase, "accent" | "ink" | "failed" | "idle"> = {
  never: "idle",
  queued: "accent",
  updating: "accent",
  fresh: "ink",
  failed: "failed",
  stopped: "idle",
};

/* ---------- motion, per dot ---------- */

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const hash = (x: number) => {
  const s = Math.sin(x * 12.9898) * 43758.5453;
  return s - Math.floor(s);
};

/** The Updating wave: cycle length, smallest dot and spread (in cycles) across the columns. */
const WAVE = { period: 1.3, min: 0.3, spread: 0.9 };

interface LiveCell extends Cell {
  el: SVGElement;
  /** Random direction and distance a failed update knocks this dot. */
  angle: number;
  reach: number;
}
interface LiveMark {
  svg: SVGSVGElement;
  body: SVGPathElement;
  outline: SVGPathElement;
  art: Artwork;
  cells: LiveCell[];
  cur: RealityModelPhase;
  prev: RealityModelPhase;
  /** 0-based step; 6 means every step is done. */
  step: number;
  /** 0..1 cross-fade from `prev` to `cur`. */
  blend: number;
  pulseT: number;
  shakeT: number;
  failT: number;
  still: boolean;
}

/** [size 0..1, opacity 0..1] for one dot in one phase. */
function evalPhase(phase: RealityModelPhase, c: Cell, m: LiveMark, t: number, still: boolean): [number, number] {
  const n = m.art.ncol;
  switch (phase) {
    case "never": {
      if (still) return [1, 0.4];
      const b = 0.5 + 0.5 * Math.cos((2 * Math.PI * t) / 4.2);
      return [0.72 + 0.28 * b, 0.3 + 0.35 * b];
    }
    case "failed":
      return [0.9, 1];
    case "stopped":
      return [0.78, 0.75];
    case "queued": {
      if (still) return [0.85, 0.5];
      const on = clamp(((t / 1.6) % 1) * (n + 0.6) - c.col, 0, 1);
      return [0.55 + 0.45 * on, 0.3 + 0.7 * on];
    }
    case "fresh": {
      if (still) return [1, 1];
      const w = 0.5 + 0.5 * Math.cos((2 * Math.PI * t) / 6.5);
      return [0.62 + 0.38 * w, 1];
    }
    case "updating": {
      const rows = m.art.rows;
      const step = m.step >= 6 ? rows : rows === 6 ? m.step : Math.round((m.step * (rows - 1)) / 5);
      if (c.row < step) return [1, 1];
      if (c.row > step) return [0.7, 0.3];
      if (still) return [1, 1];
      const w = 0.5 + 0.5 * Math.cos(2 * Math.PI * (t / WAVE.period - (c.col / (n - 1)) * WAVE.spread));
      return [WAVE.min + (1 - WAVE.min) * w, 1];
    }
  }
}

/** One inflow dip after an update finishes, about 1.4 s. */
function settlePulse(c: Cell, n: number, tau: number): number {
  const local = tau - (c.col / (n - 1)) * 0.45;
  return 1 - 0.4 * Math.sin(Math.PI * clamp(local / 0.7, 0, 1));
}

/** Overshooting ease for the knock-out of a failed update. */
const easeBack = (x: number) => 1 + 2.2 * Math.pow(x - 1, 3) + 1.2 * Math.pow(x - 1, 2);

function draw(m: LiveMark, t: number, dt: number) {
  const { still, art } = m;
  m.blend = Math.min(1, m.blend + dt / 0.6);
  const w = m.blend < 1 ? (1 - Math.cos(Math.PI * m.blend)) / 2 : 1;
  if (m.pulseT >= 0) {
    m.pulseT += dt;
    if (m.pulseT > 1.6) m.pulseT = -1;
  }

  // Stopped: the dots jolt back as if the wave hit a wall, then shake out.
  let shake = -1;
  if (!still && m.cur === "stopped") {
    if (m.shakeT < 0) m.shakeT = 0;
    m.shakeT += dt;
    shake = m.shakeT % 2.8;
  } else {
    m.shakeT = -1;
  }

  // Failed: the dots get knocked out of their grid, hold, and are pulled back.
  let scatter = 0;
  if (!still && m.cur === "failed") {
    if (m.failT < 0) m.failT = 0;
    m.failT += dt;
    const p = m.failT % 3.2;
    if (p < 0.55) scatter = easeBack(p / 0.55);
    else if (p < 2.3) scatter = 1;
    else if (p < 2.9) {
      const q = (p - 2.3) / 0.6;
      scatter = 1 - q * q * (3 - 2 * q);
    }
    scatter *= w;
  } else {
    m.failT = -1;
  }

  m.svg.dataset.tone = TONE[w < 0.5 ? m.prev : m.cur];
  const outline = (m.cur === "never" ? w : 0) + (m.prev === "never" ? 1 - w : 0);
  m.body.style.opacity = (1 - outline).toFixed(3);
  m.outline.style.opacity = outline.toFixed(3);
  const pitch = art.vb[1] / art.rows;
  const small = art.key === "s16";

  for (const c of m.cells) {
    let [size, op] = evalPhase(m.cur, c, m, t, still);
    if (w < 1) {
      const [s0, o0] = evalPhase(m.prev, c, m, t, still);
      size = s0 + (size - s0) * w;
      op = o0 + (op - o0) * w;
    }
    if (m.pulseT >= 0 && !still) size *= settlePulse(c, art.ncol, m.pulseT);
    if (c.stem) {
      size = 1;
      op = 1 - outline;
    } else if (!small) {
      // Not built yet draws the dots as rings.
      c.el.style.fillOpacity = (1 - outline).toFixed(3);
      c.el.style.strokeOpacity = outline.toFixed(3);
      if (outline > 0.001) op += (1 - op) * outline * 0.6;
    }

    let dx = 0;
    let dy = 0;
    if (shake >= 0 && !c.stem) {
      const local = shake - (art.ncol - 2 - c.col) * 0.06; // the column next to the R hits first
      if (local > 0) dx = -0.16 * pitch * Math.exp(-local * 6) * Math.sin(local * 2 * Math.PI * 8);
    }
    if (scatter && !c.stem) {
      const mag = c.reach * pitch * scatter;
      dx += Math.cos(c.angle) * mag - (0.03 * pitch * scatter * (art.ncol - 1 - c.col)) / (art.ncol - 1);
      dy = Math.sin(c.angle) * mag;
    }
    const move = dx || dy ? `translate(${dx.toFixed(3)} ${dy.toFixed(3)}) ` : "";
    const scale =
      size > 0.999 ? "" : `translate(${c.cx} ${c.cy}) scale(${size.toFixed(3)}) translate(${-c.cx} ${-c.cy})`;
    c.el.setAttribute("transform", (move + scale).trim());
    c.el.style.opacity = op.toFixed(3);
  }
}

/* ---------- one animation frame loop for every mark ---------- */

const live = new Set<LiveMark>();
let clock = 0;
let last = 0;
let frameId: number | null = null;

function tick(now: number) {
  const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
  last = now;
  clock += dt;
  for (const m of live) draw(m, clock, dt);
  frameId = live.size ? requestAnimationFrame(tick) : null;
}
function startLoop(m: LiveMark) {
  live.add(m);
  if (frameId === null) {
    last = 0;
    frameId = requestAnimationFrame(tick);
  }
}
function stopLoop(m: LiveMark) {
  live.delete(m);
  if (!live.size && frameId !== null) {
    cancelAnimationFrame(frameId);
    frameId = null;
  }
}

/* ---------- component ---------- */

export interface RealityModelMarkProps {
  phase: RealityModelPhase;
  /** 1-based step while updating. */
  step?: number | null;
  size?: number;
  /** false draws the phase without its motion, for places that only name the Reality Model. */
  animated?: boolean;
  /** Change it (a counter) to run one settle pulse, or one shake while stopped: hover and focus feedback. */
  pulse?: number;
  className?: string;
}

export function RealityModelMark({
  phase,
  step = null,
  size = 20,
  animated = true,
  pulse = 0,
  className,
}: RealityModelMarkProps) {
  const art = artworkFor(size);
  const svgRef = useRef<SVGSVGElement>(null);
  const markRef = useRef<LiveMark | null>(null);
  const zeroStep = step == null ? 0 : step - 1;

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const dots = Array.from(svg.querySelectorAll<SVGElement>("[data-cell]"));
    const m: LiveMark = {
      svg,
      body: svg.querySelector<SVGPathElement>("[data-body]")!,
      outline: svg.querySelector<SVGPathElement>("[data-outline]")!,
      art,
      cells: art.cells.map((c, i) => ({
        ...c,
        el: dots[i],
        angle: hash(i * 3.17 + 11.3) * 2 * Math.PI,
        reach: 0.05 + 0.09 * hash(i * 5.91 + 2.7),
      })),
      cur: phase,
      prev: phase,
      step: zeroStep,
      blend: 1,
      pulseT: -1,
      shakeT: -1,
      failT: -1,
      still: !animated || reduced,
    };
    markRef.current = m;
    draw(m, clock, 0);
    if (!m.still) startLoop(m);
    return () => {
      stopLoop(m);
      markRef.current = null;
    };
    // The artwork and motion setting rebuild the mark; phase and step changes blend in below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [art, animated]);

  useEffect(() => {
    const m = markRef.current;
    if (!m) return;
    m.step = zeroStep;
    if (m.cur !== phase) {
      if (phase === "fresh" && (m.cur === "updating" || m.cur === "queued")) m.pulseT = 0;
      m.prev = m.cur;
      m.cur = phase;
      m.blend = m.still ? 1 : 0;
    }
    if (m.still) draw(m, clock, 0);
  }, [phase, zeroStep]);

  useEffect(() => {
    const m = markRef.current;
    if (!m || !pulse || m.still) return;
    if (m.cur === "stopped") m.shakeT = 0;
    else m.pulseT = 0;
  }, [pulse]);

  const small = art.key === "s16";
  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${art.vb[0]} ${art.vb[1]}`}
      width={size}
      height={(size * art.vb[1]) / art.vb[0]}
      aria-hidden
      data-tone={TONE[phase]}
      className={cn(styles.mark, className)}
    >
      <path data-body d={art.body} fillRule="evenodd" />
      {art.cells.map((c, i) => {
        // Every dot but the stem's can turn into a ring; 16px dots are too small to outline.
        const ring = !c.stem && !small ? { stroke: "currentColor", strokeWidth: 1, vectorEffect: "non-scaling-stroke" as const, style: { strokeOpacity: 0 } } : {};
        return c.pts ? (
          <polygon key={i} data-cell points={c.pts.map((p) => p.join(" ")).join(" ")} {...ring} />
        ) : (
          <circle key={i} data-cell cx={c.cx} cy={c.cy} r={c.r} {...ring} />
        );
      })}
      <path
        data-outline
        d={REALITY_MODEL_OUTLINES[art.key]}
        className={styles.outline}
        strokeWidth={art.key === "large" ? 1.25 : 1}
        vectorEffect={art.key === "large" ? "non-scaling-stroke" : undefined}
        style={{ opacity: 0 }}
      />
    </svg>
  );
}

/** The mark as a plain 16px icon: up to date and still. Fits where a Lucide icon would. */
export function RealityModelGlyph({ className }: { className?: string }) {
  return <RealityModelMark phase="fresh" size={16} animated={false} className={className} />;
}
